/**
 * Protocol Parser Registry
 *
 * Manages available protocol parsers and auto-detects
 * which parser to use for incoming messages.
 */

import type { IProtocolParser, IProtocolParserRegistry } from '../types';
import { JsonProtocolParser } from './JsonProtocolParser';
import { CloudEventsParser } from './CloudEventsParser';
import { DIDCommV2Parser } from './DIDCommV2Parser';

/**
 * Default protocol parser registry
 *
 * Tries parsers in order of specificity:
 * 1. DIDComm v2 (most specific - JWE format)
 * 2. CloudEvents (field-specific)
 * 3. JSON (default fallback)
 */
export class ProtocolParserRegistry implements IProtocolParserRegistry {
  private parsers: Map<string, IProtocolParser> = new Map();

  private detectionOrder: string[] = [];

  constructor() {
    // Register default parsers in specificity order
    this.register(new DIDCommV2Parser());
    this.register(new CloudEventsParser());
    this.register(new JsonProtocolParser());
  }

  register(parser: IProtocolParser): void {
    this.parsers.set(parser.name, parser);
    // DIDComm most specific, then CloudEvents, then JSON
    if (parser.name === 'didcomm-v2') {
      this.detectionOrder.unshift(parser.name);
    } else if (parser.name === 'cloudevents') {
      const didcommIndex = this.detectionOrder.indexOf('didcomm-v2');
      if (didcommIndex >= 0) {
        this.detectionOrder.splice(didcommIndex + 1, 0, parser.name);
      } else {
        this.detectionOrder.unshift(parser.name);
      }
    } else {
      this.detectionOrder.push(parser.name);
    }
  }

  resolve(raw: string | ArrayBuffer): IProtocolParser {
    // Try in specificity order
    for (const name of this.detectionOrder) {
      const parser = this.parsers.get(name);
      if (parser && parser.canParse(raw)) {
        return parser;
      }
    }

    // Fallback to JSON parser (always available)
    const jsonParser = this.parsers.get('json');
    if (jsonParser) {
      return jsonParser;
    }

    // Should never reach here
    throw new Error('No protocol parser available');
  }

  get(name: string): IProtocolParser | undefined {
    return this.parsers.get(name);
  }
}

/**
 * Global default registry instance
 */
let defaultRegistry: ProtocolParserRegistry | null = null;

/**
 * Get or create default protocol parser registry
 */
export function getDefaultRegistry(): ProtocolParserRegistry {
  if (!defaultRegistry) {
    defaultRegistry = new ProtocolParserRegistry();
  }
  return defaultRegistry;
}
